import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../../core/global-message';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { AppActions } from '../../../../../core/util/store/actions';
import { AsyncReturnService } from '../../../../../shared';
import { CartEntryPalletFlagsConnector } from '../../connector/cart-entry-pallet-flags.connector';
import { CartEntryPalletFlagsActionTypes } from '../actions/cart-entry-pallet-flags.action';
import { CartActions } from '../actions/index';

@Injectable()
export class CartEntryPalletFlagsEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  updatePalletFlags$: Observable<
    CartActions.UpdateCartEntryPalletFlagsSuccess | CartActions.UpdateCartEntryPalletFlagsFail | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPalletFlagsActionTypes.UpdateCartEntryPalletFlags),
      map((action: CartActions.UpdateCartEntryPalletFlags) => action.payload),
      concatMap(({ actionToken, ...payload }) =>
        this.connector.update(payload.userId, payload.cartId, payload.entry, payload.palletFlags).pipe(
          map(() => {
            this.asyncReturnService.resolveToken(actionToken, true);
            return new CartActions.UpdateCartEntryPalletFlagsSuccess({
              ...payload,
            });
          }),
          catchError((error) => {
            this.asyncReturnService.resolveToken(actionToken, false);
            return from([
              new CartActions.UpdateCartEntryPalletFlagsFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new CartActions.LoadCart({
                userId: payload.userId,
                cartType: payload.cartType,
                cartId: payload.cartId,
              }),
            ]);
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updatePalletFlagsSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPalletFlagsActionTypes.UpdateCartEntryPalletFlagsSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.updateEntryPalletFlagsSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  handleFailure$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartEntryPalletFlagsActionTypes.UpdateCartEntryPalletFlagsFail),
      map((action: CartActions.UpdateCartEntryPalletFlagsFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.applyCartEntryPalletFlagsFailure_message',
              params: {
                entryNumber: +action.payload.entry.entryNumber + 1,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartEntryPalletFlagsConnector,
    private asyncReturnService: AsyncReturnService
  ) {}
}
